import DOMPurify from 'dompurify';
import { IconState } from '@/components';
import { getIconNamesFromTechnologies } from '@/features/projects/helpers';
import { ProjectItemList, TechnologyType, Location } from '@/features/projects/types';

import { Opportunity, OpportunityDetails, OpportunityItemList, Status, TypeOfRevenue } from '../types';
import { ChipColor } from '@/components/chips/types';
import { customRound, europeanFormatNumber } from '@/helpers';
import { TFunction } from 'i18next';

export function getTechnologyTypesFromOpportunity(opportunity: OpportunityItemList): TechnologyType[] {
  return opportunity.technologies.types;
}

export function getDevelopmentStatusFromOpportunity(opportunity: OpportunityItemList): string[] {
  return opportunity.technologies.state.list;
}

export function getCountriesFromOpportunity(opportunity: OpportunityItemList): string[] {
  return [...new Set(opportunity.location.map(({ country }) => country))];
}

export function getCapacityFromOpportunity(opportunity: OpportunityItemList): number {
  return opportunity.technologies.capacity || opportunity.totalMw || 0;
}

export function getColorFromOpportunityStatus(status: Status) {
  switch (status) {
    case 'draft':
      return 'disabled';
    case 'pending':
      return 'warning';
    case 'published':
      return 'info';
    case 'closed':
      return 'info';
  }
}

export function getChipFromOpportunity(opportunity: OpportunityItemList | OpportunityDetails | any) {
  if (opportunity.status.name === 'closed') {
    return {
      color: 'disabled' as ChipColor,
      name: 'closed',
    };
  }

  if (opportunity.status.name === 'published') {
    if (opportunity.isPaid) {
      return {
        color: 'gold' as ChipColor,
        name: 'premium',
      };
    }
  } else {
    return opportunity.status;
  }
}

export function getRangeOfPercentage(minimum_percentage: number, maximum_percentage: number) {
  return `${europeanFormatNumber(minimum_percentage)}% - ${europeanFormatNumber(maximum_percentage)}%`;
}
export function getIconNamesFromOpportunity(opportunity: Opportunity) {
  return [
    ...new Set(opportunity.projects.flatMap((project) => getIconNamesFromTechnologies(project.project.technologies))),
  ];
}
export function getIconStatesFromOpportunity(opportunity: Opportunity) {
  return getIconNamesFromOpportunity(opportunity).map(
    (iconName) => ({ icon: iconName, status: 'active' }) as IconState
  );
}

export function getTechnologyTypesFromOpportunityProjects(projects: ProjectItemList[]) {
  return [...new Set(projects.flatMap((project) => project.technologies.types))];
}

export function getLocationFromOpportunityProjects(projects: ProjectItemList[]) {
  const locations = projects.map((project) => project.location);
  const locationsCountries = new Set(locations.map((location) => location.country));

  function _cityInList(itemInList: Location, city: string[]) {
    return itemInList.city.some(
      (item) => item.toString().indexOf(city.join(', ')) !== -1 || city.join(', ').indexOf(item.toString()) !== -1
    );
  }

  const opportunityLocations = [] as Location[];

  locationsCountries.forEach((country) => {
    locations
      .filter((location) => location.country === country)
      .forEach((location) => {
        const itemInList = opportunityLocations.find((location) => location.country === country);
        itemInList
          ? !_cityInList(itemInList, location.city)
            ? itemInList?.city.push(...location.city)
            : null
          : opportunityLocations.push({ country: country, city: [...location.city] });
      });
  });

  return opportunityLocations;
}

export function getDevelopmentStatusFromOpportunityProjects(projects: ProjectItemList[]) {
  return [...new Set(projects.flatMap((project) => project.technologies.state.list))];
}

export function getCapacityFromOpportunityProjects(projects: ProjectItemList[]) {
  return projects.reduce((prev, project) => prev + (project.technologies.capacity ?? 0), 0);
}

export function getTotalMwFromOpportunityProjects(projects: any[]): number | undefined {
  for (const project of projects) {
    const totalMw = project.project?.portfolio_summary?.[0]?.total_mw;
    if (totalMw) return totalMw;
  }
  return undefined;
}

export function getOpportunitiesOrdered(opportunities: OpportunityItemList[]) {
  const opportunitiesPaid = opportunities.filter(
    (opportunity) => opportunity.status.name !== 'closed' && opportunity.isPaid
  );
  const opportunitiesActive = opportunities.filter(
    (opportunity) => opportunity.status.name !== 'closed' && !opportunity.isPaid
  );
  const opportunitiesClosed = opportunities.filter((opportunity) => opportunity.status.name === 'closed');

  return [
    ...opportunitiesPaid.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    ...opportunitiesActive.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    ...opportunitiesClosed.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
  ];
}

export function sanitizeUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  // 1. Trim whitespace
  let sanitized = url.trim();

  sanitized = DOMPurify.sanitize(sanitized, {
    ALLOWED_TAGS: [], // No permitir tags HTML
    ALLOWED_ATTR: [], // No permitir atributos
  });

  try {
    const urlObj = new URL(sanitized);

    if (urlObj.protocol !== 'https:') {
      return null;
    }

    const safeUrl = urlObj.toString().replace(/[<>"'`]/g, '');

    return safeUrl;
  } catch {
    return null;
  }
}

export function formatCapacity(types: string[], capacity: number, totalMw?: number): string {
  if (types.includes('topco')) {
    return totalMw ? `${europeanFormatNumber(customRound(totalMw))} MW` : '—';
  }
  if (types.includes('biogas')) {
    return capacity ? `${europeanFormatNumber(customRound(capacity))} GWh` : '—';
  }
  if (types.includes('solar')) {
    return capacity ? `${europeanFormatNumber(customRound(capacity))} MWp` : '—';
  }
  return capacity ? `${europeanFormatNumber(customRound(capacity))} MW` : '—';
}

export function getCapacityTitle(types: string[], t: TFunction): string {
  if (types.includes('topco')) return t('Total Portfolio');
  if (types.includes('biogas')) return t('Capacity');
  return t('Installed capacity');
}

export function getLocationTitle(types: string[], t: TFunction): string {
  if (types.includes('topco')) return t('Markets');
  return t('Location');
}

export function getCompanyTypeFromOpportunityProjects(projects: any[]): string | undefined {
  for (const project of projects) {
    const companyType = project.project?.technologies?.find((t: any) => t.collection === 'projects_topco_technologies')
      ?.item?.company_type;
    if (companyType) return companyType;
  }
  return undefined;
}

export function getTypeOfRevenueFromOpportunityProjects(projects: Opportunity['projects']): TypeOfRevenue[] {
  return [
    ...new Set(projects.map((p) => p.project?.other_info?.[0]?.type_of_revenue).filter(Boolean) as TypeOfRevenue[]),
  ];
}

export function getLatestActivityDate(opportunity: { createdAt: string; offers: { status: string, updatedAt: string }[] }): number {
  return opportunity.offers.reduce((latest, offer) => {
    if (offer.status === 'pending') return latest;
    return Math.max(latest, new Date(offer.updatedAt).getTime());
  }, new Date(opportunity.createdAt).getTime());
}
